import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Modal, Tabs, Table, Tag, Button, Typography, Space } from "antd"
import type { ColumnsType } from "antd/es/table"
import { SearchOutlined, FileAddOutlined, DownloadOutlined, RedoOutlined, EyeOutlined } from "@ant-design/icons"
import { logService } from "@/services/logApi"
import dayjs from "dayjs"
import { PreviewOutputModal } from "@/views/log/add-vocabulary/components/PreviewOutputModal"

const { Text } = Typography

interface UserLogsModalProps {
  open: boolean
  user: any | null
  onClose: () => void
}

export const UserLogsModal: React.FC<UserLogsModalProps> = ({ open, user, onClose }) => {
  const [activeTab, setActiveTab] = useState<string>("search_logs")
  const [searchLogPage, setSearchLogPage] = useState<number>(1)
  const [addVocabLogPage, setAddVocabLogPage] = useState<number>(1)
  const [previewModalOpen, setPreviewModalOpen] = useState<boolean>(false)
  const [selectedRecord, setSelectedRecord] = useState<any>(null)

  // 1. Search Logs Query
  const {
    data: searchLogsResult,
    isFetching: isFetchingSearchLogs,
    refetch: refetchSearchLogs,
  } = useQuery({
    queryKey: ["user_search_logs", user?.id, searchLogPage],
    queryFn: () => logService.listSearchLogs({ user_id: user?.id, page: searchLogPage, limit: 10 }),
    enabled: open && !!user?.id && activeTab === "search_logs",
  })

  // 2. Add Vocab Logs Query
  const {
    data: addVocabLogsResult,
    isFetching: isFetchingAddVocabLogs,
    refetch: refetchAddVocabLogs,
  } = useQuery({
    queryKey: ["user_add_vocab_logs", user?.id, addVocabLogPage],
    queryFn: () => logService.listAddVocabularyLogs({ user_id: user?.id, page: addVocabLogPage, limit: 10 }),
    enabled: open && !!user?.id && activeTab === "add_vocab_logs",
  })

  const getDownloadUrl = (path: string) => {
    if (!path) return ""
    if (path.startsWith("http://") || path.startsWith("https://")) {
      return path.replace(/^http:\/\//i, "https://")
    }
    return `https://cdn.jupi.vn/${path.replace(/^\/+/, "")}`
  }

  // Search logs columns
  const searchLogColumns: ColumnsType<any> = [
    { title: "ID", dataIndex: "id", width: 60, align: "center" },
    {
      title: "Từ khóa (Query)",
      dataIndex: "query",
      render: (val) => <span className="font-semibold text-primary">{val}</span>,
    },
    {
      title: "Loại Match",
      dataIndex: "match_type",
      width: 110,
      align: "center",
      render: (val) => {
        const color = val === "exact" ? "green" : val === "fuzzy" ? "orange" : "red"
        return <Tag color={color}>{val?.toUpperCase() || "NONE"}</Tag>
      },
    },
    { title: "Từ kết quả", dataIndex: "matched_term", render: (val) => val || "-" },
    { title: "Phương thức", dataIndex: "commit_method", width: 110, render: (val) => val || "-" },
    {
      title: "Thời gian",
      dataIndex: "created_at",
      width: 140,
      align: "center",
      render: (val) => (val ? dayjs(val).format("DD/MM/YYYY HH:mm") : "-"),
    },
  ]

  // Add vocab logs columns
  const addVocabLogColumns: ColumnsType<any> = [
    { title: "ID", dataIndex: "id", width: 60, align: "center" },
    {
      title: "Nguồn (Source)",
      dataIndex: "source_type",
      width: 120,
      render: (val) => {
        const color = val === "quick" ? "blue" : val === "upload" ? "purple" : "cyan"
        return <Tag color={color}>{val?.toUpperCase() || val}</Tag>
      },
    },
    {
      title: "Bộ từ (Set đích)",
      dataIndex: ["set", "title"],
      render: (val, row) => val || row.set?.name || <Text type="secondary">-</Text>,
    },
    { title: "Xem trước", dataIndex: "total_previewed_items", width: 90, align: "right" },
    { title: "Đã lưu", dataIndex: "total_committed_items", width: 90, align: "right" },
    { title: "AI Sinh", dataIndex: "ai_generated_count", width: 80, align: "right" },
    {
      title: "Input / Output",
      key: "input_output",
      width: 150,
      align: "center",
      render: (_: any, record: any) => {
        const inputPath = record.raw_input_path
        const outputPath = record.output_path

        if (!inputPath && !outputPath) return <Text type="secondary">-</Text>

        return (
          <Space size={4} split={<span className="select-none text-gray-300">/</span>}>
            {inputPath ? (
              <Button
                size="small"
                type="link"
                icon={<DownloadOutlined />}
                href={getDownloadUrl(inputPath)}
                download={inputPath.split("/").pop() || "raw_input.txt"}
                target="_blank"
                rel="noopener noreferrer"
                className="px-1 text-xs"
              >
                Input
              </Button>
            ) : (
              <span className="select-none text-gray-400 text-xs">-</span>
            )}

            {outputPath ? (
              <Button
                size="small"
                type="primary"
                ghost
                icon={<EyeOutlined />}
                onClick={() => {
                  setSelectedRecord(record)
                  setPreviewModalOpen(true)
                }}
                className="px-2 text-xs"
              >
                Output
              </Button>
            ) : (
              <span className="select-none text-gray-400 text-xs">-</span>
            )}
          </Space>
        )
      },
    },
    {
      title: "Thời gian",
      dataIndex: "created_at",
      width: 140,
      align: "center",
      render: (val) => (val ? dayjs(val).format("DD/MM/YYYY HH:mm") : "-"),
    },
  ]

  return (
    <Modal
      title={
        <div className="flex items-center justify-between pr-6">
          <div className="flex items-center gap-2">
            <span>Nhật ký hoạt động (Logs):</span>
            <span className="font-semibold text-primary">
              {user?.name || user?.username || user?.email || "Người dùng"}
            </span>
            <Tag color="blue">#{user?.id}</Tag>
          </div>
          <Button
            size="small"
            icon={<RedoOutlined />}
            onClick={() => {
              if (activeTab === "search_logs") refetchSearchLogs()
              else refetchAddVocabLogs()
            }}
            loading={isFetchingSearchLogs || isFetchingAddVocabLogs}
          >
            Làm mới
          </Button>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={[
        <Button key="close" type="primary" onClick={onClose}>
          Đóng
        </Button>,
      ]}
      width={920}
      destroyOnClose
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: "search_logs",
            label: (
              <span>
                <SearchOutlined className="mr-1" />
                Log tìm kiếm
              </span>
            ),
            children: (
              <Table
                rowKey="id"
                columns={searchLogColumns}
                dataSource={searchLogsResult?.data || []}
                loading={isFetchingSearchLogs}
                pagination={{
                  current: searchLogPage,
                  pageSize: 10,
                  total: searchLogsResult?.total || 0,
                  onChange: (p) => setSearchLogPage(p),
                  size: "small",
                }}
                size="small"
                scroll={{ x: 750 }}
              />
            ),
          },
          {
            key: "add_vocab_logs",
            label: (
              <span>
                <FileAddOutlined className="mr-1" />
                Log thêm từ vựng
              </span>
            ),
            children: (
              <Table
                rowKey="id"
                columns={addVocabLogColumns}
                dataSource={addVocabLogsResult?.data || []}
                loading={isFetchingAddVocabLogs}
                pagination={{
                  current: addVocabLogPage,
                  pageSize: 10,
                  total: addVocabLogsResult?.total || 0,
                  onChange: (p) => setAddVocabLogPage(p),
                  size: "small",
                }}
                size="small"
                scroll={{ x: 850 }}
              />
            ),
          },
        ]}
      />

      <PreviewOutputModal
        open={previewModalOpen}
        outputPath={selectedRecord?.output_path}
        onClose={() => {
          setPreviewModalOpen(false)
          setSelectedRecord(null)
        }}
        record={selectedRecord}
      />
    </Modal>
  )
}

export default UserLogsModal
